import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';

const TechFixDemo: React.FC = () => {
  useEffect(() => {
    document.title = 'TechFix Solutions - Demo | BusinessWebsitePro';
  }, []);

  const services = [
    { name: 'IT Consulting', icon: 'ri-lightbulb-line', description: 'Strategic technology planning and implementation' },
    { name: 'Cloud Solutions', icon: 'ri-cloud-line', description: 'Secure cloud migration and management services' },
    { name: 'Cybersecurity', icon: 'ri-shield-check-line', description: 'Comprehensive security audits and protection' },
    { name: 'Network Setup', icon: 'ri-router-line', description: 'Enterprise-grade network infrastructure' },
    { name: '24/7 Support', icon: 'ri-customer-service-line', description: 'Round-the-clock technical assistance' },
    { name: 'Data Backup', icon: 'ri-database-line', description: 'Automated backup and disaster recovery' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                Technology Services Demo
              </span>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">TechFix Solutions</h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional IT services website with client portal, service showcase, and support ticket system. Perfect for technology consulting businesses.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap">
                  Get Started
                </button>
                <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 whitespace-nowrap">
                  View Services
                </button>
              </div>
            </div>
            <div className="w-full h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/558c770a9a877b7822dd8c9bdcb7265d.jpg" 
                alt="TechFix Solutions Website" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive IT solutions for your business</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.name}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <button className="text-blue-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all duration-300 cursor-pointer whitespace-nowrap">
                  Learn More
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by Businesses</h2>
            <p className="text-xl text-gray-600">Our track record speaks for itself</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-5xl font-bold text-blue-600 mb-3">500+</p>
              <p className="text-lg text-gray-600">Clients Served</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-blue-600 mb-3">99.9%</p>
              <p className="text-lg text-gray-600">Uptime Guarantee</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-blue-600 mb-3">24/7</p>
              <p className="text-lg text-gray-600">Support Available</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-blue-600 mb-3">15 Years</p>
              <p className="text-lg text-gray-600">Industry Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Website Features</h2>
            <p className="text-xl text-gray-600">Built for IT service providers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Client Portal</h3>
              <p className="text-gray-600">Secure login area for clients to manage services and tickets</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-ticket-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Support Tickets</h3>
              <p className="text-gray-600">Integrated ticketing system for efficient support management</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-text-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Knowledge Base</h3>
              <p className="text-gray-600">Comprehensive documentation and FAQ section</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Need a Professional IT Services Website?</h2>
          <p className="text-xl text-gray-600 mb-8">
            We'll build you a powerful website with client portal, support system, and all the features you need.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-[#14B8A6] text-white px-10 py-5 rounded-xl font-semibold hover:bg-[#0d9488] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Get Your Website
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TechFixDemo;
